import { useCallback, useEffect, useMemo, useState } from "react";
import { Search, Loader2, Pencil, Network, Users, ChevronRight, UserPlus, BarChart3, IdCard } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import OSLayout from "../../components/os/OSLayout";
import { api, mediaUrl } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import EmployeePicker from "../../components/os/EmployeePicker";
import { Avatar, AvatarFallback, AvatarImage } from "../../components/ui/avatar";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";

const input = "w-full bg-white border border-navy/15 rounded-lg px-3 py-2.5 outline-none focus:border-violet text-sm";
const label = "text-xs font-medium text-navy/60 mb-1 block";

function EmpAvatar({ e, size = "h-11 w-11" }) {
  return (
    <Avatar className={`${size} shrink-0`}>
      {e.avatar ? <AvatarImage src={mediaUrl(e.avatar)} alt={e.name} /> : null}
      <AvatarFallback className="text-xs bg-violet/10 text-violet">
        {(e.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("")}
      </AvatarFallback>
    </Avatar>
  );
}

function flattenOrg(roots) {
  const out = [];
  const walk = (nodes, depth) => {
    (nodes || []).forEach((n) => {
      out.push({ ...n, depth });
      if ((n.children || []).length) walk(n.children, depth + 1);
    });
  };
  walk(roots, 0);
  return out;
}

export default function Directory() {
  const { user } = useAuth();
  const [tab, setTab] = useState("dir");
  const [employees, setEmployees] = useState([]);
  const [scopeLabel, setScopeLabel] = useState("");
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [area, setArea] = useState("");
  const [org, setOrg] = useState({ roots: [], count: 0 });
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [invite, setInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({ username: "", role: "cliente", puesto: "", area: "" });
  const [inviting, setInviting] = useState(false);
  const [metric, setMetric] = useState("all");
  const [birthdayAuto, setBirthdayAuto] = useState(false);
  const [bMsg, setBMsg] = useState("");
  const [bBenefit, setBBenefit] = useState("");

  const canManage = ["admin", "ceo", "director"].includes(user?.role) || user?.is_super;

  const load = useCallback(() => {
    setLoading(true);
    api.get("/directory?include_inactive=true")
      .then((r) => { setEmployees(r.data.employees || []); setScopeLabel(r.data.scope || ""); })
      .catch(() => setEmployees([]))
      .finally(() => setLoading(false));
    api.get("/people/org-chart").then((r) => setOrg(r.data)).catch(() => {});
    api.get("/people/analytics").then((r) => setAnalytics(r.data)).catch(() => {});
    api.get("/people/birthdays/setting").then((r) => { setBirthdayAuto(!!r.data.auto_post); setBMsg(r.data.message || ""); setBBenefit(r.data.benefit || ""); }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleBirthday = async () => {
    const next = !birthdayAuto;
    setBirthdayAuto(next);
    try { await api.put("/people/birthdays/setting", { auto_post: next, message: bMsg, benefit: bBenefit }); toast.success(next ? "Felicitaciones automáticas activadas" : "Desactivadas"); }
    catch { setBirthdayAuto(!next); toast.error("Error"); }
  };
  const saveGift = async () => {
    try { await api.put("/people/birthdays/setting", { auto_post: birthdayAuto, message: bMsg, benefit: bBenefit }); toast.success("Regalo de cumpleaños guardado"); }
    catch { toast.error("Error"); }
  };

  const doInvite = async () => {
    if (!inviteForm.username.trim()) return toast.error("Indica el usuario a invitar");
    setInviting(true);
    try {
      const r = await api.post("/people/companies/invite", inviteForm);
      toast.success(`${r.data.name} agregado a la empresa`);
      setInvite(false);
      setInviteForm({ username: "", role: "cliente", puesto: "", area: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al invitar"); }
    finally { setInviting(false); }
  };

  const areas = useMemo(() => Array.from(new Set(employees.map((e) => e.area).filter(Boolean))).sort(), [employees]);
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return employees.filter((e) => {
      if (area && e.area !== area) return false;
      if (metric === "lideres" && !e.es_lider) return false;
      if (metric === "con_jefe" && !e.reporta_a) return false;
      if (metric === "sin_jefe" && (e.reporta_a || e.estado !== "activo")) return false;
      if (metric === "inactivos" && e.estado !== "inactivo") return false;
      if (metric === "all" && e.estado !== "activo") return false;
      if (!term) return true;
      return [e.name, e.first_name, e.last_name, e.puesto, e.area, e.email]
        .some((v) => (v || "").toLowerCase().includes(term));
    });
  }, [employees, q, area, metric]);

  const sinJefe = useMemo(() => employees.filter((e) => !e.reporta_a && e.estado === "activo"), [employees]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkMgr, setBulkMgr] = useState(null);
  const [bulkSaving, setBulkSaving] = useState(false);
  const bulkAssign = async () => {
    if (!bulkMgr) return toast.error("Elige al responsable");
    setBulkSaving(true);
    try {
      const r = await api.post("/people/memberships/bulk-reporta", { reporta_a: bulkMgr.id });
      toast.success(`Responsable asignado a ${r.data.updated} colaborador(es) — toda solicitud ya tiene aprobador`);
      setBulkOpen(false); setBulkMgr(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setBulkSaving(false); }
  };

  const openEdit = (e) => {
    setEditing(e);
    setForm({
      puesto: e.puesto || "", area: e.area || "", departamento: e.departamento || "",
      unidad_negocio: e.unidad_negocio || "", equipo: e.equipo || "",
      nivel_jerarquico: e.nivel_jerarquico || "", es_lider: !!e.es_lider,
      reporta_a: e.reporta_a || null, status: e.estado === "activo" ? "active" : "inactive",
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/people/memberships/${editing.id}`, {
        ...form,
        nivel_jerarquico: form.nivel_jerarquico === "" ? null : Number(form.nivel_jerarquico),
      });
      toast.success("Ficha actualizada");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };

  return (
    <OSLayout title="Directorio Corporativo">
      <div className="max-w-6xl">
        <div className="flex items-center gap-2 mb-6">
          <button data-testid="tab-directory" onClick={() => setTab("dir")}
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm transition-colors ${tab === "dir" ? "bg-navy text-cream" : "text-navy/60 hover:bg-navy/5"}`}>
            <Users className="w-4 h-4" /> Directorio
          </button>
          <button data-testid="tab-orgchart" onClick={() => setTab("org")}
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm transition-colors ${tab === "org" ? "bg-navy text-cream" : "text-navy/60 hover:bg-navy/5"}`}>
            <Network className="w-4 h-4" /> Organigrama
          </button>
        </div>

        {tab === "dir" && (
          <>
            {analytics && (
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3 mb-5" data-testid="people-analytics">
                {[
                  { l: "Colaboradores", v: analytics.headcount, m: "all" },
                  { l: "Líderes", v: analytics.lideres, m: "lideres" },
                  { l: "Con jefe", v: analytics.con_jefe_asignado, m: "con_jefe" },
                  { l: "Sin jefe", v: analytics.sin_jefe_asignado, m: "sin_jefe" },
                  { l: "Presentes hoy", v: analytics.asistencia_hoy?.presentes_ahora ?? 0, nav: "/os/workforce?tab=attendance" },
                  { l: "Inactivos", v: analytics.inactivos, m: "inactivos" },
                ].map((k) => (
                  <button key={k.l} data-testid={`metric-${k.m || "presentes"}`}
                    onClick={() => { if (k.nav) window.location.assign(k.nav); else { setMetric(k.m); setTab("dir"); } }}
                    className={`text-left bg-white border rounded-xl p-3 transition-colors hover:border-violet ${metric === k.m ? "border-violet ring-1 ring-violet/30" : "border-navy/10"}`}>
                    <div className="text-[11px] text-navy/45 flex items-center gap-1"><BarChart3 className="w-3 h-3" /> {k.l}</div>
                    <div className="text-2xl font-medium text-navy mt-1">{k.v}</div>
                  </button>
                ))}
              </div>
            )}
            {metric !== "all" && (
              <button onClick={() => setMetric("all")} data-testid="metric-clear" className="text-xs text-violet mb-3 hover:underline">← Ver todos los colaboradores</button>
            )}
            {canManage && birthdayAuto && (
              <div className="mb-5 bg-violet/5 border border-violet/15 rounded-xl p-4" data-testid="birthday-gift-box">
                <div className="text-sm font-medium text-navy mb-2 flex items-center gap-2">🎁 Regalo de cumpleaños de la empresa</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input data-testid="birthday-message" className="w-full bg-white border border-navy/15 rounded-lg px-3 py-2.5 outline-none focus:border-violet text-sm"
                    placeholder="Mensaje del CEO (opcional)" value={bMsg} onChange={(e) => setBMsg(e.target.value)} />
                  <input data-testid="birthday-benefit" className="w-full bg-white border border-navy/15 rounded-lg px-3 py-2.5 outline-none focus:border-violet text-sm"
                    placeholder="Beneficio / regalo (ej. día libre + bono)" value={bBenefit} onChange={(e) => setBBenefit(e.target.value)} />
                </div>
                <div className="flex justify-end mt-3">
                  <Button data-testid="birthday-gift-save" onClick={saveGift} className="bg-navy text-cream hover:bg-navy-soft h-8 text-sm">Guardar regalo</Button>
                </div>
              </div>
            )}
            <div className="flex flex-wrap items-center gap-3 mb-5">
              <div className="flex items-center gap-2 bg-white border border-navy/15 rounded-xl px-3 py-2 flex-1 min-w-[240px]">
                <Search className="w-4 h-4 text-navy/40" />
                <input data-testid="directory-search" value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder="Buscar por nombre, apellido, cargo o área…"
                  className="flex-1 bg-transparent text-sm outline-none placeholder:text-navy/40" />
              </div>
              <select data-testid="directory-area-filter" value={area} onChange={(e) => setArea(e.target.value)}
                className="bg-white border border-navy/15 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-violet">
                <option value="">Todas las áreas</option>
                {areas.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
              <Badge variant="outline" className="border-navy/15 text-navy/50">{filtered.length} colaboradores</Badge>
              {canManage && (
                <label data-testid="birthday-toggle" className="flex items-center gap-2 text-xs text-navy/60 cursor-pointer select-none bg-white border border-navy/15 rounded-full px-3 py-2">
                  <input type="checkbox" checked={birthdayAuto} onChange={toggleBirthday} className="accent-violet" />
                  🎂 Felicitar cumpleaños auto
                </label>
              )}
              {canManage && sinJefe.length > 0 && (
                <Button data-testid="bulk-reporta-open" onClick={() => setBulkOpen(true)} variant="outline"
                  className="border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100 gap-2 h-9">
                  <Network className="w-4 h-4" /> Asignar responsable a {sinJefe.length} sin jefe
                </Button>
              )}
              {canManage && (
                <Button data-testid="invite-open" onClick={() => setInvite(true)} className="bg-navy text-cream hover:bg-navy-soft gap-2 h-9">
                  <UserPlus className="w-4 h-4" /> Invitar a esta empresa
                </Button>
              )}
            </div>

            {loading ? (
              <div className="flex items-center gap-2 text-navy/40 py-10"><Loader2 className="w-4 h-4 animate-spin" /> Cargando directorio…</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="directory-grid">
                {filtered.map((e) => (
                  <div key={e.id} data-testid={`directory-card-${e.id}`}
                    className="bg-white border border-navy/10 rounded-2xl p-4 flex items-start gap-3 hover:border-violet/40 transition-colors">
                    <EmpAvatar e={e} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-navy truncate">{e.name}</span>
                        {e.es_lider && <Badge className="bg-gold/15 text-gold border-none text-[10px]">Líder</Badge>}
                      </div>
                      <div className="text-xs text-navy/60 truncate">{e.puesto || "—"}</div>
                      <div className="text-xs text-navy/40 truncate">{e.area || "Sin área"}</div>
                      <div className="mt-2 flex items-center gap-2">
                        <span className={`inline-block w-1.5 h-1.5 rounded-full ${e.estado === "activo" ? "bg-emerald-500" : "bg-navy/25"}`} />
                        <span className="text-[11px] text-navy/40 capitalize">{e.estado}</span>
                        {e.reporta_a_nombre && <span className="text-[11px] text-navy/40 truncate">· reporta a {e.reporta_a_nombre}</span>}
                        {!e.reporta_a && e.estado === "activo" && <span className="text-[11px] text-amber-600 truncate" data-testid={`sin-jefe-${e.id}`}>· sin responsable</span>}
                      </div>
                    </div>
                    {canManage && (
                      <button data-testid={`directory-edit-${e.id}`} onClick={() => openEdit(e)}
                        className="text-navy/30 hover:text-violet"><Pencil className="w-4 h-4" /></button>
                    )}
                    <Link to={`/os/expediente/${e.id}`} data-testid={`directory-record-${e.id}`}
                      className="text-navy/30 hover:text-violet" title="Ver expediente"><IdCard className="w-4 h-4" /></Link>
                  </div>
                ))}
                {filtered.length === 0 && <div className="text-navy/40 text-sm py-6">Sin colaboradores.</div>}
              </div>
            )}
          </>
        )}

        {tab === "org" && (
          <div className="bg-white border border-navy/10 rounded-2xl p-5" data-testid="orgchart">
            <div className="text-sm text-navy/50 mb-3">{org.count} colaboradores en la empresa activa</div>
            {org.roots.length === 0 ? (
              <div className="text-navy/40 text-sm">Aún no hay estructura organizacional definida.</div>
            ) : (
              <div className="space-y-0.5">
                {flattenOrg(org.roots).map((n) => (
                  <div key={n.id} className="flex items-center gap-2 py-1.5" style={{ paddingLeft: n.depth * 22 }} data-testid={`org-node-${n.id}`}>
                    {n.depth > 0 && <ChevronRight className="w-3.5 h-3.5 text-navy/25 shrink-0" />}
                    <EmpAvatar e={n} size="h-8 w-8" />
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-navy truncate">{n.name}</div>
                      <div className="text-xs text-navy/50 truncate">{[n.puesto, n.area].filter(Boolean).join(" · ") || "—"}</div>
                    </div>
                    {n.es_lider && <Badge className="bg-gold/15 text-gold border-none text-[10px]">Líder</Badge>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <Dialog open={invite} onOpenChange={setInvite}>
        <DialogContent className="max-w-md" data-testid="invite-dialog">
          <DialogHeader><DialogTitle>Invitar colaborador a esta empresa</DialogTitle>
            <DialogDescription>Agrega a una identidad OMNIA existente a la empresa activa. Podrá cambiar entre sus empresas con el selector global.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><label className={label}>Usuario OMNIA (sin @omnia.os)</label><input data-testid="invite-username" className={input} value={inviteForm.username} onChange={(e) => setInviteForm({ ...inviteForm, username: e.target.value })} placeholder="p. ej. leonel" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={label}>Puesto</label><input data-testid="invite-puesto" className={input} value={inviteForm.puesto} onChange={(e) => setInviteForm({ ...inviteForm, puesto: e.target.value })} /></div>
              <div><label className={label}>Área</label><input data-testid="invite-area" className={input} value={inviteForm.area} onChange={(e) => setInviteForm({ ...inviteForm, area: e.target.value })} /></div>
            </div>
            <div>
              <label className={label}>Rol en la empresa</label>
              <select data-testid="invite-role" className={input} value={inviteForm.role} onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}>
                {["cliente", "ventas", "operaciones", "compras", "marketing", "contador", "director", "ceo", "admin"].map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="ghost" onClick={() => setInvite(false)}>Cancelar</Button>
            <Button data-testid="invite-submit" onClick={doInvite} disabled={inviting} className="bg-navy text-cream hover:bg-navy-soft">
              {inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Invitar"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={bulkOpen} onOpenChange={(o) => !o && setBulkOpen(false)}>
        <DialogContent className="max-w-md" data-testid="bulk-reporta-dialog">
          <DialogHeader><DialogTitle>Organigrama de aprobación</DialogTitle>
            <DialogDescription>Asigna un jefe directo a los {sinJefe.length} colaboradores activos sin responsable, para que toda solicitud (vacaciones, gastos, documentos) tenga aprobador.</DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <EmployeePicker value={bulkMgr?.id || null} testid="bulk-reporta-picker"
              placeholder="Elegir responsable por defecto…"
              onChange={(emp) => setBulkMgr(emp)} />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setBulkOpen(false)}>Cancelar</Button>
            <Button data-testid="bulk-reporta-save" onClick={bulkAssign} disabled={bulkSaving}
              className="bg-navy text-cream hover:bg-navy-soft">
              {bulkSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : `Asignar a ${sinJefe.length}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg" data-testid="edit-membership-dialog">
          <DialogHeader><DialogTitle>Ficha laboral — {editing?.name}</DialogTitle>
            <DialogDescription>Actualiza la información laboral y la jerarquía organizacional del colaborador.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className={label}>Reporta a (Jefe Directo)</label>
              <EmployeePicker value={form.reporta_a} testid="reporta-a-picker"
                placeholder="Seleccionar jefe directo…"
                onChange={(emp) => setForm((f) => ({ ...f, reporta_a: emp ? emp.id : null }))} />
            </div>
            <div><label className={label}>Puesto</label><input data-testid="edit-puesto" className={input} value={form.puesto} onChange={(e) => setForm({ ...form, puesto: e.target.value })} /></div>
            <div><label className={label}>Área</label><input data-testid="edit-area" className={input} value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} /></div>
            <div><label className={label}>Unidad de Negocio</label><input className={input} value={form.unidad_negocio} onChange={(e) => setForm({ ...form, unidad_negocio: e.target.value })} /></div>
            <div><label className={label}>Departamento</label><input className={input} value={form.departamento} onChange={(e) => setForm({ ...form, departamento: e.target.value })} /></div>
            <div><label className={label}>Equipo</label><input className={input} value={form.equipo} onChange={(e) => setForm({ ...form, equipo: e.target.value })} /></div>
            <div><label className={label}>Nivel Jerárquico</label><input type="number" className={input} value={form.nivel_jerarquico} onChange={(e) => setForm({ ...form, nivel_jerarquico: e.target.value })} /></div>
            <div className="flex items-center gap-2 pt-6">
              <input id="es_lider" type="checkbox" data-testid="edit-es-lider" checked={form.es_lider} onChange={(e) => setForm({ ...form, es_lider: e.target.checked })} />
              <label htmlFor="es_lider" className="text-sm text-navy/70">Es líder</label>
            </div>
            <div>
              <label className={label}>Estado</label>
              <select data-testid="edit-status" className={input} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                <option value="active">Activo</option>
                <option value="inactive">Inactivo</option>
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="ghost" onClick={() => setEditing(null)}>Cancelar</Button>
            <Button data-testid="save-membership" onClick={save} disabled={saving} className="bg-navy text-cream hover:bg-navy-soft">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </OSLayout>
  );
}
